/**
 * The basic map figure (D34): the flat grid, boxes and links, as native
 * SVG. Split out of the panel when the hologram arrived (D169); the panel
 * picks the figure by the layout's `style`, and this one is byte-for-byte
 * what the panel drew before.
 *
 * @packageDocumentation
 */

import { type MapLayout } from '@orpheus/engine-runtime';

/** The plan's layout — the engine's flat layout, tagged `basic` (D209: laid out where it is drawn, no longer on the wire). */
export type BasicMapLayout = Extract<MapLayout, { readonly style: 'basic' }>;

/** Props for {@link BasicMapFigure}. */
export interface BasicMapFigureProps {
	readonly layout: BasicMapLayout;
	/** The figure's accessible name — what the pack calls its map (D189). */
	readonly title: string;
}

/**
 * Render the flat map.
 *
 * @param props - {@link BasicMapFigureProps}
 * @returns The figure
 */
export const BasicMapFigure = ({ layout, title }: BasicMapFigureProps) => (
	<svg
		aria-labelledby="map-panel-svg-title map-panel-svg-desc"
		className="orpheus-map map-panel__figure"
		// Intrinsic size from the layout: without it the SVG stretches to
		// its container and magnifies every label with it, which is what
		// made the map unreadable. CSS may shrink it, never blow it up.
		height={layout.height}
		role="img"
		viewBox={`0 0 ${String(layout.width)} ${String(layout.height)}`}
		width={layout.width}
	>
		<title id="map-panel-svg-title">{title}</title>
		<desc id="map-panel-svg-desc">{layout.description}</desc>
		{layout.edges.map((edge) => (
			<line
				className="orpheus-map__edge"
				key={`edge-${String(edge.x1)}-${String(edge.y1)}-${String(edge.x2)}-${String(edge.y2)}`}
				stroke="var(--orpheus-map-edge, #4a5a52)"
				strokeWidth={2}
				x1={edge.x1}
				x2={edge.x2}
				y1={edge.y1}
				y2={edge.y2}
			/>
		))}
		{layout.nodes.map((node) => (
			<g
				className={`orpheus-map__node orpheus-map__node--${node.knowledge}${node.isActive ? ' orpheus-map__node--active' : ''}`}
				key={`node-${String(node.x)}-${String(node.y)}`}
			>
				<rect
					fill={node.knowledge === 'visited' ? 'var(--orpheus-map-fill, #16221c)' : 'transparent'}
					height={node.height}
					rx={3}
					stroke={nodeStroke(node.isActive, node.knowledge)}
					strokeDasharray={node.knowledge === 'visited' ? undefined : '5 4'}
					strokeWidth={node.isActive ? 2.5 : 1.5}
					width={node.width}
					x={node.x}
					y={node.y}
				/>
				<BasicMapLabel node={node} />
			</g>
		))}
	</svg>
);

/**
 * One node's label: a `<text>` at its one baseline, or — a name wrapped by
 * the layout (D222) — a `<text>` holding one `<tspan>` per line, each on its
 * own baseline at the same center. The figure draws what it is handed.
 *
 * @param props - The node whose label to draw
 * @returns The label
 */
const BasicMapLabel = ({ node }: { readonly node: BasicMapLayout['nodes'][number] }) => {
	const [first, ...rest] = node.lines;
	// Presentation ATTRIBUTES, so they are the fallback for serializations
	// that ship without the stylesheet; any CSS rule on `.orpheus-map__label`
	// outranks them.
	const presentation = {
		className: 'orpheus-map__label',
		fill: 'var(--orpheus-map-text, #cfe3d6)',
		fontFamily: 'var(--orpheus-map-font, ui-monospace, monospace)',
		fontSize: 11,
		textAnchor: 'middle',
	} as const;

	if (rest.length === 0) {
		return (
			<text {...presentation} x={node.labelX} y={first.y}>
				{first.text}
			</text>
		);
	}

	return (
		<text {...presentation} x={node.labelX}>
			{node.lines.map((line) => (
				<tspan key={line.y} x={node.labelX} y={line.y}>
					{line.text}
				</tspan>
			))}
		</text>
	);
};

/** The stroke variable for one node's knowledge tier (active outranks the rest). */
const nodeStroke = (isActive: boolean, knowledge: BasicMapLayout['nodes'][number]['knowledge']): string => {
	if (isActive) {
		return 'var(--orpheus-map-active, #9fe0b6)';
	}

	return knowledge === 'unknown' ? 'var(--orpheus-map-unknown, #5d6b63)' : 'var(--orpheus-map-node, #86a693)';
};
